import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { QueryClient } from "@tanstack/react-query";
import { getTenantSlug } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// ── Types ────────────────────────────────────────────────────────────────────
interface TenantSessionValue {
  /** Current active tenant slug (reactive — updates trigger re-renders) */
  tenantSlug: string;
  /** Composite key: "${tenantSlug}::${userId}" — changes on tenant or user switch */
  sessionId: string;
  /** The QueryClient for the current session. Fresh instance per identity change. */
  queryClient: QueryClient;
  /** Switch to a different tenant. Updates localStorage + state; triggers new QueryClient. */
  switchTenant: (slug: string) => void;
}

const TenantSessionContext = createContext<TenantSessionValue | null>(null);

// ── Helper: create a new QueryClient with sensible defaults ─────────────────
function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
      },
    },
  });
}

// ── Provider ─────────────────────────────────────────────────────────────────
export function TenantSessionProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useAuth();

  // Tenant slug as React state — initialised from localStorage / URL / env
  const [tenantSlug, setTenantSlug] = useState<string>(() => getTenantSlug());

  // Composite session key
  const sessionId = `${tenantSlug}::${user?.id ?? ""}`;

  // Track previous sessionId so we know when to swap the QueryClient
  const prevSessionId = useRef<string>(sessionId);
  const queryClientRef = useRef<QueryClient>(makeQueryClient());

  // When the session identity changes, replace the QueryClient entirely.
  // This runs synchronously during render (before children mount) so there is
  // ZERO window where a child component could read stale cached data.
  if (prevSessionId.current !== sessionId) {
    // Session identity changed — create fresh QueryClient
    // Tear down old client (cancel pending queries, free memory)
    try {
      queryClientRef.current.clear();
    } catch {
      /* ignore */
    }
    queryClientRef.current = makeQueryClient();
    prevSessionId.current = sessionId;
  }

  const switchTenant = useCallback((slug: string) => {
    const normalised = slug.trim().toLowerCase();
    if (!normalised) return;
    localStorage.setItem("besthr_active_tenant", normalised);
    setTenantSlug(normalised);
  }, []);

  const value = useMemo<TenantSessionValue>(
    () => ({
      tenantSlug,
      sessionId,
      queryClient: queryClientRef.current,
      switchTenant,
    }),
    [tenantSlug, sessionId, switchTenant],
  );

  return (
    <TenantSessionContext.Provider value={value}>
      {children}
    </TenantSessionContext.Provider>
  );
}

// ── Hook ─────────────────────────────────────────────────────────────────────
// eslint-disable-next-line react-refresh/only-export-components
export function useTenantSession(): TenantSessionValue {
  const ctx = useContext(TenantSessionContext);
  if (!ctx) {
    throw new Error(
      "useTenantSession must be used within a TenantSessionProvider",
    );
  }
  return ctx;
}
